import React from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { IconCube, IconEye, IconX } from "@tabler/icons-react";
import ButtonMenu from "components/ButtonMenu";
import clsx from "clsx";
import { searchStore } from "features/ProductSearch/SearchStore";
import Button from "components/Button";
import { COEStore } from "features/ComponentEditor/ComponentEditorStore";
import { productUsageStore } from "features/ProductUsage/ProductUsageStore";
import { ProductActionMode } from "utils";

interface SaveLoadingProps {
  state: boolean;
  match?: boolean;
  add?: boolean;
}

export default observer(function EditorHeader({
  saveAndClose,
  disabled,
}: {
  saveAndClose: (doAdd?: boolean, doMatch?: boolean) => Promise<void>;
  disabled?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [saveLoading, setSaveLoading] = React.useState<SaveLoadingProps>({
    state: false,
    match: false,
  });

  const buttonItems = [
    {
      name: COEStore.openedSearchInCE
        ? t("productEditor.saveAndAdd")
        : t("productEditor.saveAndMapp"),
      onClick: () =>
        COEStore.openedSearchInCE
          ? onClickMenu(t("productEditor.saveAndAdd"), true, false)
          : onClickMenu(t("productEditor.saveAndMapp"), false, true),
      disabled: disabled || saveLoading.state,
    },
    {
      name: t("commons.save"),
      onClick: () => onClickMenu(t("commons.save")),
      disabled: disabled || saveLoading.state,
    },
  ];

  const [clickedButton, setClickedButton] = React.useState<string>(
    buttonItems[0].name
  );

  const doSave = async (
    menuName: string,
    doAdd?: boolean,
    doMatch?: boolean
  ) => {
    setSaveLoading({ state: true, match: doMatch, add: doAdd });
    setClickedButton(menuName);
    await saveAndClose(doAdd, doMatch);
    setSaveLoading({ state: false });
  };

  const onClickMenu = (
    menuName: string,
    doAdd?: boolean,
    doMatch?: boolean
  ) => {
    // When the edit impacts buildings/components, confirm via the usage
    // drill-down before saving; the modal's footer triggers the actual save.
    if (
      productUsageStore.isUsedAnywhere &&
      location.state?.mode === ProductActionMode.Edit &&
      !productUsageStore.modal.open
    ) {
      productUsageStore.openSaveConfirmation(
        productUsageStore.isUsedInComponents ? "components" : "buildings",
        () => doSave(menuName, doAdd, doMatch)
      );
      return;
    }
    doSave(menuName, doAdd, doMatch);
  };

  const cancelButton = () => {
    return (
      <div
        onClick={() => navigate(-1)}
        className={clsx(
          "border border-gray-300 rounded-md min-w-9 min-h-9 flex items-center justify-center",
          "ml-3 hover:bg-gray-100 text-gray-700 hover:text-gray-900 cursor-pointer",
          { "pointer-events-none opacity-50": saveLoading.state }
        )}
      >
        <IconX className="min-w-6 min-h-6" stroke={2} />
      </div>
    );
  };

  const showSaveButtons = () => {
    let showSaveButtonOnly =
      searchStore.usingOrganisationSearch &&
      !location.pathname.includes("mapping-editor");

    if (location.state?.isDetails) {
      showSaveButtonOnly = true;
    }

    if (showSaveButtonOnly) {
      return (
        <>
          <Button
            onClick={() => onClickMenu(t("commons.save"))}
            disable={disabled}
            tooltip={disabled ? t("productEditor.saveButtonTooltip") : ""}
            className="!h-9"
          >
            {t("commons.save")}
          </Button>
        </>
      );
    }

    return (
      <>
        <ButtonMenu
          current={buttonItems.find((b) => b.name === clickedButton)}
          items={buttonItems}
          loading={saveLoading.state}
          disabled={disabled}
          tooltip={disabled ? t("productEditor.saveButtonTooltip") : ""}
          className="flex lg:hidden !h-9"
          buttonClassName="!h-9"
        />
        {/* Save */}
        <Button
          key={buttonItems[1].name}
          type="secondary"
          onClick={buttonItems[1].onClick}
          loading={saveLoading.state && !saveLoading.match && !saveLoading.add}
          disable={buttonItems[1].disabled}
          tooltip={disabled ? t("productEditor.saveButtonTooltip") : ""}
          className="hidden lg:flex !h-9"
        >
          {buttonItems[1].name}
        </Button>
        {/* Save & Mapp || Save & Add */}
        <Button
          key={buttonItems[0].name}
          width="fit-content"
          className="ml-3 hidden lg:flex !h-9"
          onClick={buttonItems[0].onClick}
          loading={saveLoading.state && (saveLoading.match || saveLoading.add)}
          disable={buttonItems[0].disabled}
          tooltip={disabled ? t("productEditor.saveButtonTooltip") : ""}
        >
          {buttonItems[0].name}
        </Button>
      </>
    );
  };

  return (
    <div
      className={clsx(
        "flex w-full px-6 py-3 items-center justify-between border-b border-gray-100 bg-teal-50 h-[60px]"
      )}
    >
      <div id="editor-title" className="flex">
        <div className="flex justify-between items-center">
          <IconCube
            className="bg-teal-300 w-8 h-8 rounded-full p-1.5 mr-3"
            stroke={2}
          />
          <div className="text-xl font-semibold text-black mr-6">
            {t("productEditor.buildingProductEditor")}
          </div>
        </div>
      </div>
      <div className="flex items-center">
        <IconEye
          className="text-indigo-700 w-6 h-6 mr-3 cursor-pointer hover:text-indigo-900 hidden"
          stroke={2}
        />
        {showSaveButtons()}
        {cancelButton()}
      </div>
    </div>
  );
});
